import { AddIcon, CloseIcon, cn, DeleteIcon, EditIcon, Text } from '@viapeople/components'
import {
	EdgeLabelRenderer,
	type EdgeProps,
	getBezierPath,
	Handle,
	type NodeProps,
	Position,
	useUpdateNodeInternals,
} from '@xyflow/react'
import { useEffect } from 'react'
import { useCycleStepsStore } from './store/useCycleStepsStore'
import { type EdgeData, type NodeData, StepType } from './types'

export function CustomHandle({
	type,
	position,
	id,
	className = '',
	title,
}: {
	type: 'source' | 'target'
	position: Position
	id?: string
	className?: string
	title?: string
}) {
	const getTransform = () => {
		switch (position) {
			case Position.Top:
				return 'translateY(-110%)'
			case Position.Right:
				return 'translateX(110%)'
			case Position.Bottom:
				return 'translateY(110%)'
			case Position.Left:
				return 'translateX(-110%)'
			default:
				return 'translateY(110%)'
		}
	}

	const isReject = id?.includes('reject')

	const handleStyle = {
		transform: getTransform(),
		zIndex: 1,
		width: 16,
		height: 16,
		borderRadius: 16,
		backgroundColor: isReject ? 'rgba(239, 68, 68, 0.1)' : 'transparent',
		borderWidth: 1,
		borderColor: isReject ? '#EF4444' : '#93C5FD',
	}

	return (
		<Handle
			id={id || `${type}-${position}`}
			type={type}
			position={position}
			style={handleStyle}
			className={className}
			title={title}>
			<AddIcon
				size={12}
				className={`pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 ${
					isReject ? 'fill-error-400' : 'fill-primary-400'
				}`}
			/>
		</Handle>
	)
}

export function CustomNodeComponent({
	id,
	data: nodeData,
	selected,
}: {
	id: string
	data: NodeData
	selected: boolean
}) {
	const { diagramDefinition, edges, nodes } = useCycleStepsStore()
	const updateNodeInternals = useUpdateNodeInternals()

	// Check if this step type has a reject action defined
	const rejectActionInfo = (() => {
		if (!diagramDefinition?.[nodeData.stepType as string]) return { hasReject: false, hasValidTarget: false }

		const rejectAction = diagramDefinition[nodeData.stepType as string].possibleActions.find(
			(action) => action.key === 'reject',
		)

		if (!rejectAction) return { hasReject: false, hasValidTarget: false }

		// If allowedNextSteps is null, reject can go anywhere
		if (rejectAction.allowedNextSteps === null) return { hasReject: true, hasValidTarget: true }

		// Check if any node in the diagram is a valid reject target
		const hasValidTarget = nodes.some(
			(node) => node.id !== id && rejectAction.allowedNextSteps?.includes(node.data.stepType as string),
		)

		return { hasReject: true, hasValidTarget }
	})()

	const hasReject = rejectActionInfo.hasReject
	const hasValidRejectTarget = rejectActionInfo.hasValidTarget

	const dragSourceInfo = nodeData.dragSourceInfo as {
		nodeId: string
		handleId: string
		stepType: StepType
	} | null

	const isRejectTarget = edges.some((edge) => edge.target === id && edge.data?.key === 'reject')

	const shouldShowTargetReject = (() => {
		if (isRejectTarget) {
			return true
		}

		if (dragSourceInfo?.handleId.includes('reject')) {
			if (dragSourceInfo.nodeId === id) {
				return false
			}

			if (diagramDefinition && dragSourceInfo.stepType) {
				const sourceStepActions = diagramDefinition[dragSourceInfo.stepType]?.possibleActions || []
				const rejectAction = sourceStepActions.find((action) => action.key === 'reject')

				if (rejectAction?.allowedNextSteps) {
					return rejectAction.allowedNextSteps.includes(nodeData.stepType as string)
				}
			}
		}

		return false
	})()

	// Show the reject target handle if this node doesn't have its own reject action,
	// OR if any other node in the diagram can reject to this node's step type.
	// The handle must be in the DOM before a drag starts for React Flow to register it.
	const isValidRejectTargetForOtherNodes = (() => {
		if (!diagramDefinition || !nodeData.stepType) return false
		return nodes.some((node) => {
			if (node.id === id || !node.data.stepType) return false
			const stepDef = diagramDefinition[node.data.stepType]
			if (!stepDef) return false
			const rejectAction = stepDef.possibleActions.find((a) => a.key === 'reject')
			if (!rejectAction) return false
			if (rejectAction.allowedNextSteps === null) return true
			return rejectAction.allowedNextSteps.includes(nodeData.stepType as string)
		})
	})()

	const shouldAlwaysShowRejectTarget = !hasReject || isValidRejectTargetForOtherNodes

	useEffect(() => {
		updateNodeInternals(id)
	}, [id, updateNodeInternals])

	return (
		<div>
			<CustomHandle type='target' position={Position.Top} id='target-approve' className='approve-handle' />

			<div
				className={cn(
					'relative bg-white border rounded-md px-3 py-2 min-w-40',
					selected ? 'border-primary-300 ring-1 ring-primary-300' : 'border-neutral-200',
				)}>
				<div className='flex items-center justify-between'>
					<div>{nodeData.label}</div>
					<div className='flex items-center'>
						<button
							type='button'
							className='ml-2 text-neutral-500 hover:text-primary-500 cursor-pointer'
							onClick={(e) => {
								e.stopPropagation()
								nodeData.onEdit?.(id)
							}}>
							<EditIcon className='fill-neutral-500' />
						</button>
						<button
							type='button'
							className='ml-2 text-neutral-500 hover:fill-error-600 cursor-pointer'
							onClick={(e) => {
								e.stopPropagation()
								nodeData.onDelete?.(id)
							}}>
							<DeleteIcon className='fill-neutral-500' />
						</button>
					</div>
				</div>
			</div>

			<CustomHandle type='source' position={Position.Bottom} id='source-approve' className='approve-handle' />

			{hasReject && (
				<CustomHandle
					type='source'
					position={Position.Left}
					id='source-reject'
					className={`reject-handle ${!hasValidRejectTarget ? 'opacity-40 cursor-not-allowed' : ''}`}
					title={!hasValidRejectTarget ? 'Add a Manager Evaluation step to enable rejection' : undefined}
				/>
			)}

			{(shouldShowTargetReject || shouldAlwaysShowRejectTarget) && (
				<CustomHandle
					type='target'
					position={Position.Left}
					id='target-reject'
					className={`reject-handle ${!shouldShowTargetReject ? 'opacity-20' : ''}`}
				/>
			)}
		</div>
	)
}

export function FixedNodeComponent({ data }: NodeProps) {
	const nodeData = data as NodeData

	return (
		<div>
			{nodeData.stepType === StepType.CYCLE_END && (
				<CustomHandle type='target' position={Position.Top} id='target-approve' />
			)}

			<div className='relative bg-neutral-200 border border-neutral-300 rounded-md px-3 py-2'>
				<div className='flex items-center justify-between'>
					<div>{nodeData.label}</div>
				</div>
			</div>
			{nodeData.stepType === StepType.CYCLE_START && (
				<CustomHandle type='source' position={Position.Bottom} id='source-approve' />
			)}
		</div>
	)
}

export function CustomEdgeComponent({
	id,
	sourceX,
	sourceY,
	targetX,
	targetY,
	sourcePosition,
	targetPosition,
	style,
	data,
	markerEnd,
}: EdgeProps) {
	const edgeData = data as EdgeData

	const isReject = edgeData?.key === 'reject'

	const edgeColor = isReject ? '#EF4444' : '#10B981'
	const [edgePath, labelX, labelY] = getBezierPath({
		sourceX,
		sourceY,
		sourcePosition,
		targetX,
		targetY,
		targetPosition,
	})

	return (
		<>
			<path
				id={id}
				className='react-flow__edge-path'
				d={edgePath}
				strokeWidth={1.5}
				stroke={edgeColor}
				markerEnd={markerEnd}
				style={style}
			/>
			{edgeData?.label && (
				<EdgeLabelRenderer>
					<div
						style={{
							position: 'absolute',
							transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
							pointerEvents: 'all',
							backgroundColor: 'white',
							padding: '2px 4px',
							borderRadius: '4px',
							fontSize: 12,
							fontWeight: 500,
							border: `1px solid ${edgeColor}`,
							color: edgeColor,
						}}
						className='nodrag nopan'>
						{edgeData.label}
						<button
							type='button'
							className='absolute flex justify-center items-center -translate-y-1/2 top-1/2 -right-5 w-4 h-4 rounded-full bg-white border border-neutral-300 cursor-pointer'
							onClick={(e) => {
								e.stopPropagation()
								edgeData.onDelete?.(id)
							}}>
							<CloseIcon size={14} className='fill-error-300 hover:fill-error-600' />
						</button>
					</div>
				</EdgeLabelRenderer>
			)}
		</>
	)
}

export function StepsPaletteCard({
	icon,
	displayName,
	onClick,
	stepType,
}: {
	icon: React.ReactNode
	displayName: string
	onClick: () => void
	stepType?: string
}) {
	return (
		<button
			type='button'
			data-testid={stepType ? `step-palette-${stepType}` : undefined}
			className='flex items-center border border-neutral-200 rounded-md px-3 py-2 mb-2 cursor-pointer hover:bg-neutral-50'
			onClick={onClick}>
			<div className='mr-2 text-primary-500 flex items-center'>{icon}</div>
			<Text variant='xsmall'>{displayName}</Text>
		</button>
	)
}
